/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Identifier rules", description = "An overview of Hightouch's identity resolution features and implementation process.", lastUpdatedDate = new Date(1730160000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Identifier rules",
        description: "An overview of Hightouch's identity resolution features and implementation process.",
        lastUpdatedDate: "Tue Oct 29 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Merge rules",
          "depth": 0,
          "id": "merge-rules"
        }, {
          "value": "Limit rules",
          "depth": 0,
          "id": "limit-rules"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      strong: "strong",
      h2: "h2",
      code: "code",
      img: "img",
      h4: "h4",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Identity resolution",
        extraText: ". You can use it with or without Customer Studio."
      }), "\n", _jsxs(_components.p, {
        children: ["To set up identity resolution rules, you must first configure your input models and select the appropriate identifiers. Please review the information in the ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/identity-resolution/model-configuration",
          children: _jsx(_components.strong, {
            children: "Model Configuration"
          })
        }), " page before proceeding."]
      }), "\n", _jsx(_components.p, {
        children: "This page goes through the merge and limit rule configuration:"
      }), "\n", _jsx(_components.h2, {
        id: "merge-rules",
        children: "Merge rules"
      }), "\n", _jsxs(_components.p, {
        children: ["Merge rules define how the identity graph should match rows to profiles and find connections between rows. For example, two events may share the same ", _jsx(_components.code, {
          children: "anonymous_id"
        }), " even though one has a ", _jsx(_components.code, {
          children: "user_id"
        }), " and ", _jsx(_components.code, {
          children: "email"
        }), " while the other doesn't. Matching on ", _jsx(_components.code, {
          children: "anonymous_id"
        }), " tells us that those events refer to the same person and enables you to associate customer activities before they identified themselves through a form fill or purchase."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "identity-resolution/idr-merge-rules.png",
          alt: "Merge rules"
        })
      }), "\n", _jsx(_components.p, {
        children: "When a new row gets incorporated into the identity graph, the graph first tries to match it against an existing profile based on the merge rules. If no match is found, then a new profile gets created using the identifiers in the row. If a match is found, the row gets merged into the matching profile."
      }), "\n", _jsx(_components.h2, {
        id: "limit-rules",
        children: "Limit rules"
      }), "\n", _jsx(_components.p, {
        children: "Limit rules allow you to prevent merging records if they would violate a business rule that's important for the profiles you want to use for activation."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, if you never want to merge two records if they have different ", _jsx(_components.code, {
          children: "user_id"
        }), " values, then you can specify a limit rule of 1 ", _jsx(_components.code, {
          children: "user_id"
        }), " per record."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "identity-resolution/idr-limit-rules.png",
          alt: "Limit rule"
        })
      }), "\n", _jsx(_components.h4, {
        id: "prioritizing-identifiers",
        children: "Prioritizing identifiers"
      }), "\n", _jsx(_components.p, {
        children: "The priority order in merge rules determine how rows get merged into a profile if a row introduces a limit violation. The identity graph run processes each identifier type in sequence from highest to lowest priority and checks in between evaluations for each identifier type whether any profiles violate a limit rule and, if so, resolve the violation before moving on to the next identifier."
      }), "\n", _jsx(_components.p, {
        children: "When a higher priority identifier's limit gets violated, the profile gets re-processed, and the identifier value that caused the merge resulting in a violation gets dropped. This effectively breaks out a profile that violates a limit rule into multipe profiles that no longer violate the limit rule."
      }), "\n", _jsx(_components.p, {
        children: "When a lower priority identifier's limit gets violated, the profile will only keep the first N identifier values where N is the limit value."
      }), "\n", _jsx(_components.p, {
        children: "For example, imagine you have the following data:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Timestamp"
            }), _jsx(_components.th, {
              children: "Email"
            }), _jsx(_components.th, {
              children: "Anonymous ID"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:01 PM"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "ANON001"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:02 PM"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "ANON001"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:03 PM"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:john.doe@acme.com",
                children: "john.doe@acme.com"
              })
            }), _jsx(_components.td, {
              children: "ANON001"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:04 PM"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "ANON001"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:05 PM"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "ANON002"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:06 PM"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:john@dundermifflin.com",
                children: "john@dundermifflin.com"
              })
            }), _jsx(_components.td, {
              children: "ANON002"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:07 PM"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "ANON002"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:08 PM"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:carl.smith@acme.com",
                children: "carl.smith@acme.com"
              })
            }), _jsx(_components.td, {
              children: "ANON002"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["If your identifier priorities are Email first and Anonymous ID second, and you've set a limit 1 rule on Email, then the row with ", _jsx(_components.code, {
          children: "ANON002"
        }), " and ", _jsx(_components.code, {
          children: "carl.smith@acme.com"
        }), " results in a limit violation when merged into the the profile, ", _jsx(_components.code, {
          children: "HT5"
        }), ", containing the ", _jsx(_components.code, {
          children: "ANON002"
        }), " events since it would contain two different emails. In this case, the row with ", _jsx(_components.code, {
          children: "carl.smith@acme.com"
        }), " would create a new profile instead, ", _jsx(_components.code, {
          children: "HT8"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Timestamp"
            }), _jsx(_components.th, {
              children: "HT ID"
            }), _jsx(_components.th, {
              children: "Email"
            }), _jsx(_components.th, {
              children: "Anonymous ID"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:01 PM"
            }), _jsx(_components.td, {
              children: "HT1"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "ANON001"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:02 PM"
            }), _jsx(_components.td, {
              children: "HT1"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "ANON001"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:03 PM"
            }), _jsx(_components.td, {
              children: "HT1"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:john.doe@acme.com",
                children: "john.doe@acme.com"
              })
            }), _jsx(_components.td, {
              children: "ANON001"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:04 PM"
            }), _jsx(_components.td, {
              children: "HT1"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "ANON001"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:05 PM"
            }), _jsx(_components.td, {
              children: "HT5"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "ANON002"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:06 PM"
            }), _jsx(_components.td, {
              children: "HT5"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:john@dundermifflin.com",
                children: "john@dundermifflin.com"
              })
            }), _jsx(_components.td, {
              children: "ANON002"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:07 PM"
            }), _jsx(_components.td, {
              children: "HT5"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "ANON002"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "12:08 PM"
            }), _jsx(_components.td, {
              children: "HT8"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "mailto:carl.smith@acme.com",
                children: "carl.smith@acme.com"
              })
            }), _jsx(_components.td, {
              children: "ANON002"
            })]
          })]
        })]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
